import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectorRef } from '@angular/core';
import { EntitySelectorData, EntitySelectorDialogComponent } from '../../components/entities/entity-selector.dialog.component';
import { openDialogGetResult } from '../../helpers/angular-helpers';
import { Wrapper } from 'src/app/components/wrapper.component';
import { ItemStatus, itemStatus, StatusType } from 'globals/store/share.data';
import { Dictionary } from '@ngrx/entity';
import { ConfirmDeleteData, openConfirmDeleteGetResult } from '../../components/dialog.confirmDelete.component';
import { colorsByPublicEditProfile, performerDisplayTypes } from 'src/app/store/performers.era';
import { assetListReselector, entitiesReselector } from 'src/app/store/reselectors/entity.reselector';
import { Asset } from 'globals/store/assets.data';
import { ManageAssetComponent, ManageAssetData } from './manage-asset.component';
import { TypedEntity } from 'globals/store/entity.shared.data';
import { Ranking } from 'globals/store/events.data';
import { AddManageWrapperComponent } from 'src/app/components/wrappers/add-manage-wrapper.component';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { AssetLinkComponent } from './asset-link.component';
import { BriefActions } from 'globals/store/brief.data';
import { jsonCopyObject } from 'globals/helpers/helpers';
import { IdsToItemsPipe } from 'src/app/services/pipes/sortByIds.pipe';
import { StatusWrapperComponent } from 'src/app/components/wrappers/status-wrapper.component';
import { AssetCardComponent } from 'src/app/components/cards/asset-card.component';
import { titleCase } from 'src/app/helpers/helpers';
import { MatFormFieldModule } from '@angular/material/form-field';

type AssetDotId = 'remove' | 'delete' | 'manage'
interface AssetDotOption { name: string, id: AssetDotId }

@Component({
    selector: 'assets',
    templateUrl: './assets.html',
    styleUrls: ['./assets.scss'],
    standalone: true,
    imports: [ MatFormFieldModule, AddManageWrapperComponent, StatusWrapperComponent, AssetCardComponent, TitleCasePipe, IdsToItemsPipe, AssetLinkComponent, CommonModule, 

    ],
})
export class AssetsComponent extends Wrapper<AssetsComponent> implements OnInit {
  @Input() assetIds: string[]
  @Input() title = 'Assets'
  @Input() titleIcon = 'EVENT_INFORMATION24'
  @Input() readOnly = false
  @Input() addLabel = '+ ADD AN ASSET'
  @Input() type: 'widePod' | 'informable' = 'widePod'
  @Input() entityHolderName = 'session'
  @Input() defaultIcon: string = null
  @Input() associatedId: string = null
  @Input() hideTitle = false

  @Output() idsAdded: EventEmitter<string[]> = new EventEmitter()
  @Output() idRemoved: EventEmitter<string> = new EventEmitter()

  @Input() colorsByStatus: any = colorsByPublicEditProfile // can't type this grr
  @Input() statusOptions: StatusType[] = itemStatus

  fullAssetList: Asset[] = []
  assets: Dictionary<Asset>
  itemStatus = itemStatus
  displayType = performerDisplayTypes
  allEntities: Dictionary<TypedEntity> = {}
  assetCategories: Dictionary<Ranking> = {}
  
  briefActions: BriefActions // for use in manage actions
  threeDotOptions: AssetDotOption[] = [{ name: 'Remove asset from event', id: 'delete' }, { name: 'Manage asset', id: 'manage' }]

  constructor(public changeDetectorRef: ChangeDetectorRef) {
    super()
  }

  ngOnInit(): void {
    this.selectProp(assetListReselector, null, 'fullAssetList')
    this.selectProp(entitiesReselector, null, 'allEntities')
    this.subProp(this.store.select('assets', 'entities'), 'assets')
    this.subProp(this.store.select('assets', 'assetCategories'), 'assetCategories')

    const name = this.associatedId ? 
      `Disassociate asset from ${this.entityHolderName}` :
      `Remove asset from ${this.entityHolderName}`
    this.threeDotOptions.unshift({ name, id: 'remove'})
  }

  dotMenuSelection(asset: Asset, { event, option }: { event: any, option: AssetDotId }) {
    switch (option) {
      case 'remove':
        if (this.associatedId) {
          delete asset.associatedId
          this.assetService.upsertAsset(asset)
        } else this.idRemoved.emit(asset.id)
        break
      case 'delete':
        this.deleteWithConfirm(asset, event, 'program')
        break
      case 'manage':
        this.editAsset(asset)
        break;
      default:
        console.log(`Missing selection [${option}] from three dot menu`)
        break;
    }
  }

  changeStatus(asset: Asset, status: StatusType) {
    asset.status = status as ItemStatus
    console.log({ asset, status })
    this.assetService.upsertAsset(asset) 
  }

  changeReadyStatus(asset: Asset, ready: boolean) {
    console.log({ asset, ready })
    asset.ready = ready
    this.assetService.upsertAsset(asset) 
  }

  async editAsset(asset: Asset) {
    const data: ManageAssetData = { assetId: asset.id }
    this.changeDetectorRef.detach()
    const mac = await import('./manage-asset.component').then(m => m.ManageAssetComponent)
    await openDialogGetResult(this.dialog, mac, { disableClose: true, autoFocus: false, data, panelClass: 'full-screen-modal' })
    //await openDialogGetResult(this.dialog, ManageAssetComponent, { disableClose: true, autoFocus: false, data, panelClass: 'full-screen-modal' })
    this.changeDetectorRef.reattach()
  }


  async deleteWithConfirm(asset: Asset, event, type: 'program' | 'exhibitor') {
    const rect = event.target.getBoundingClientRect();
    const data: ConfirmDeleteData = {
      title: 'Remove ' + asset.name + ' from this event',
      deleteButtonText: 'REMOVE',
      rect,
      bottomOffset: -45,
      dialog: this.dialog
    }
    const del = await openConfirmDeleteGetResult(data)
    if (!del) return
    await this.assetService.deleteAssets([asset.id])
  }

  async addAsset(ev) {
    const assets = this.fullAssetList.filter(({ id }) => !this.assetIds.includes(id))
    const disabledEntityMessages: Dictionary<string> = {}
    if (this.associatedId) assets.filter(a => !!a.associatedId).forEach(a => {
      const entity = this.allEntities[a.associatedId]
      if (!entity) return
      disabledEntityMessages[a.id] = `${entity.name} (${entity.type})`
    })

    console.log({ disabledEntityMessages })

    const data: EntitySelectorData = { entities: assets, entityName: 'Asset', searchPlaceholder: 'Search or add asset', rect: (<HTMLElement>ev.target).getBoundingClientRect(), entityHolderName: this.entityHolderName, defaultIcon: this.defaultIcon, disabledEntityMessages }
    const component = await import('../../components/entities/entity-selector.dialog.component').then(m => m.EntitySelectorDialogComponent)
    const passedSelectedAssets = await openDialogGetResult(this.dialog, component, { width: '300px', data, disableClose: true, backdropClass: 'clear' })
    const selectedAssets = jsonCopyObject(passedSelectedAssets) as string[];
    console.log({ selectedAssets })
    if (!selectedAssets) return
    if (this.associatedId) { // right now can only add new assets through associated assets, not deselect directly
      const assets = selectedAssets.map(id => ({...this.assets[id]}))
      assets.forEach(a => a.associatedId = this.associatedId)
      this.assetService.upsertAssets(assets)
    } else this.idsAdded.emit(selectedAssets)
  }

}

